import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { runnerLanguages, videoOutputs } from '@/consts';
import useLanguageDisplayNames from '@/hooks/useLanguageDisplayNames';

/**
 * A run's stations, laid out as a small grid inside the schedule's players column: one line per
 * station, reading station number, who sits there, what they play on, and how it's captured.
 *
 * Runs that never had their run of show filled in fall back to the plain list of player names,
 * so a partly configured schedule still reads as a schedule.
 *
 * @param {import("prop-types").InferProps<typeof RunOfShowCellPropTypes>} props
 * @param {object} props
 * @param props.run - The run to lay out, read for its stations, players and platform
 * @param props.stationsCount - How many stations the show is physically set up with
 */
export default function RunOfShowCell({ run, stationsCount }) {
  const { t } = useTranslation();
  const playerNames = [run.player1, run.player2, run.player3, run.player4];
  const savedStations = run.stations ?? [];

  const displayNames = useLanguageDisplayNames();
  const languageLabel = value => {
    const { translationKey } = runnerLanguages.find(language => language.value === value) ?? {};

    return translationKey ? t(translationKey) : displayNames.of(value);
  };

  /** Every station the show has, so the same station number sits on the same line run to run */
  const stations = Array.from({ length: stationsCount }, (unused, index) => {
    const number = index + 1;
    const saved = savedStations.find(station => station.station === number);

    return { ...saved, station: number };
  });

  return (
    <Box
      sx={{
        display: 'grid',
        rowGap: 0.25,
        columnGap: 1.5,
        // Each of station, player, language, platform and video output takes only the room it needs
        gridTemplateColumns: 'repeat(5, max-content)',
      }}
    >
      {stations.map(station => {
        const playerName = station.playerSlot ? playerNames[station.playerSlot - 1] : '';
        const videoOutput = videoOutputs.find(output => output.value === station.videoOutput);

        // An unused station keeps its line, faded, rather than shifting the others up.
        // Carried by every cell of the line: a wrapper would have to be `display: contents`,
        // which draws no box of its own for an opacity to apply to.
        const cellSx = { whiteSpace: 'nowrap', opacity: playerName ? 1 : 0.5 };
        /** The platform alone doesn't say whether it's the real console or a PC */
        const emulatorTag = station.emulator ? ` (${t('run.emu')})` : '';

        return (
          <Fragment key={station.station}>
            <Typography variant="inherit" sx={cellSx}>
              {t('run.station')} {station.station}
            </Typography>
            <Typography variant="inherit" sx={cellSx}>
              {playerName || t('runOfShow.noPlayer')}
            </Typography>
            <Typography variant="inherit" sx={cellSx}>
              {playerName && languageLabel(station.language ?? 'unknown')}
            </Typography>
            <Typography variant="inherit" sx={cellSx}>
              {playerName && run.platform + emulatorTag}
            </Typography>
            <Typography variant="inherit" sx={cellSx}>
              {videoOutput?.translationKey ? t(videoOutput.translationKey) : videoOutput?.label}
            </Typography>
          </Fragment>
        );
      })}
    </Box>
  );
}

const RunOfShowCellPropTypes = {
  run: PropTypes.shape({
    combinedPlayers: PropTypes.string,
    platform: PropTypes.string,
    player1: PropTypes.string,
    player2: PropTypes.string,
    player3: PropTypes.string,
    player4: PropTypes.string,
    stations: PropTypes.arrayOf(
      PropTypes.shape({
        emulator: PropTypes.bool,
        language: PropTypes.string,
        playerSlot: PropTypes.number,
        station: PropTypes.number,
        videoOutput: PropTypes.string,
      }).isRequired,
    ),
  }).isRequired,
  stationsCount: PropTypes.number.isRequired,
};
RunOfShowCell.propTypes = RunOfShowCellPropTypes;
